import Section from '../../../components/Main/Section/Section';
import styled from 'styled-components';
import Filter from '../../../components/Filter/Filter';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import Chip from '../../../components/Chip/Chip';
import SliderStarsAsLabels from '../../../components/Sliders/StarsAsLabels/SliderStarsAsLabels';
import PlatformSelect from '../../../components/PlatformSelect/PlatformSelect';
import MonitoringLabelFilter from '../../../components/MonitoringLabelFilter/MonitoringLabelFilter';
import { IPlatform } from '../../../shared/interfaces/IPlatform';
import PlatformService from '../../../services/PlatformService';
import { CurrentViewContext } from '../../../providers/CurrentViewProvider';
import ActionBar from '../../../components/ActionBar/ActionBar';
import { Add } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ContentContainer from '../../../components/Containers/ContentContainer';
import LeftContainer from '../../../components/Containers/LeftContainer';
import RightContainer from '../../../components/Containers/RightContainer';
import SortContainer from '../../../components/Containers/SortContainer';
import { IMonitoring } from '../../../shared/interfaces/IMonitoring';
import ReviewMonitoringService from '../../../services/ReviewMonitoringService';
import Platform from '../../../components/Platform/Platform';
import ScoreService from '../../../services/ScoreService';
import { IScoreEntry } from '../../../shared/interfaces/IScoreEntry';
import { AuthContext } from '../../../providers/AuthProvider';
import MonitoringNoContent from '../../../assets/images/monitoring_no_content.png';
import NumberUtils from '../../../shared/NumberUtils';
import Confirm from '../../../components/Confirm/Confirm';
import EditMonitoringCard from '../../../components/EditMonitoringCard/EditMonitoringCard';
import GroupService from '../../../services/GroupService';
import { IGroup } from '../../../shared/interfaces/IGroup';

const AddEditContainer = styled.div`
  margin-left: auto;
`;

const PlatformContainer = styled.div`
  display: grid;
  grid-template: auto/10px 75px repeat(5, auto);
`;

const Platforms = () => {
  const { setCurrentView } = useContext(CurrentViewContext);
  const { hasRole, exceedsLimit } = useContext(AuthContext);
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [monitoring, setMonitoring] = useState<IMonitoring[]>([]);
  const [platforms, setPlatforms] = useState<IPlatform[]>([]);
  const [monitoredPlatforms, setMonitoredPlatforms] = useState<IMonitoring[]>([]);
  const [lastTotalScores, setLastTotalScores] = useState<IScoreEntry[]>([]);
  // Loaded once, unlike `monitoring`, so the label filter keeps offering every entry when a group is picked.
  const [monitoringLabelOptions, setMonitoringLabelOptions] = useState<IMonitoring[]>([]);

  const [loading, setLoading] = useState(false);

  const [sort, setSort] = useState('');

  const [filterByCompanyName, setFilterByCompanyName] = useState<IMonitoring | null>(null);
  const [filterStars, setFilterStars] = useState<number[]>([1, 5]);
  const [filterByPlatform, setFilterByPlatform] = useState<string>('');
  const [filterByType, setFilterByType] = useState<MonitoringFilterType>('all');
  const [showZeroReviews, setShowZeroReviews] = useState<boolean>(false);
  const [filterByGroup, setFilterByGroup] = useState<string>('');
  const [groups, setGroups] = useState<IGroup[]>([]);
  const [editMonitoringEntry, setEditMonitoringEntry] = useState<IMonitoring | null>(null);
  const [deleteMonitoringId, setDeleteMonitoringId] = useState<string | null>(null);

  const isInitialMount = useRef(true);

  const filterByTypeLabel = {
    own: t('own'),
    benchmarking: t('benchmarking'),
    all: t('all_records'),
  }[filterByType];

  const handleFilterStarsChange = (event: Event, newValue: number | number[]) => {
    setShowZeroReviews(true);
    setFilterStars(newValue as number[]);
  };

  const getRecordCount = () => {
    if (!filterByPlatform) {
      return `${platforms.length} ${platforms.length === 1 ? t('platform') : t('platforms')} - ${
        filteredPlatforms.length
      } ${filteredPlatforms.length === 1 ? t('entry') : t('entries')}`;
    } else {
      return `${filteredPlatforms.length} ${
        filteredPlatforms.length === 1 ? t('entry') : t('entries')
      }`;
    }
  };

  const getTotalScoreAndReviewCount = useCallback(
    (monitoringEntry: IMonitoring) => {
      let score: number;
      let reviewCount: number;
      let growthPercentage: number;
      let created: string;
      let employeeReviewCount: number;
      let applicantReviewCount: number;

      const matches = (kind: string) => {
        return lastTotalScores.filter(
          (company) =>
            company.company === monitoringEntry.company &&
            company.kind === kind &&
            (company.platform ||
              PlatformService.getPlatformName(monitoringEntry.platformId, platforms)) ===
              PlatformService.getPlatformName(monitoringEntry.platformId, platforms)
        );
      };

      score = matches('total')[0]?.score.score ?? monitoringEntry.lastScore;
      reviewCount = matches('total')[0]?.score.reviewCount ?? monitoringEntry.lastReviewCount;
      growthPercentage = matches('total')[0]?.growthPercentage ?? monitoringEntry.growthPercentage;
      created = matches('total')[0]?.createdAt;

      // No employee score kind is fetched any more (769968-A) - the count comes from the
      // monitoring entry, which the backend now fills from the total row's total_employee.
      employeeReviewCount = monitoringEntry.employeeReviewCount ?? 0;
      applicantReviewCount =
        matches('applicant')[0]?.score.reviewCount ?? monitoringEntry.applicantReviewCount;

      return {
        score,
        reviewCount,
        growthPercentage,
        employeeReviewCount,
        applicantReviewCount,
        created,
      };
    },
    [lastTotalScores, platforms]
  );

  useEffect(() => {
    setCurrentView(t('nav_platforms'));
    document.title = `${t('app_branding')} ${t('nav_platforms')}`;
  }, [setCurrentView, t]);

  useEffect(() => {
    const controller = new AbortController();

    const getPlatforms = async () => {
      const response = await PlatformService.getPlatforms(setLoading, controller.signal);

      if (!Array.isArray(response)) return;

      setPlatforms(response);
    };

    const getMonitoring = async () => {
      const data = await ReviewMonitoringService.getMonitoring(
        true,
        'both',
        setLoading,
        controller.signal
      );

      if (!data || 'success' in data) return;

      const monitoringEntries: IMonitoring[] = data;

      setPlatforms((prevState) => {
        return [...prevState].filter((v) => monitoringEntries.find((m) => m.platformId === v.id));
      });
      setMonitoringLabelOptions(monitoringEntries);
      setMonitoring(monitoringEntries);
      setMonitoredPlatforms(monitoringEntries);
    };

    const loadGroups = async () => {
      const groupsData = await GroupService.getGroups();
      setGroups(groupsData);
    };

    (async () => {
      await getPlatforms();
      await getMonitoring();
      await loadGroups();
    })();

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const controller = new AbortController();
    (async () => {
      const data = await ReviewMonitoringService.getMonitoring(
        true,
        'both',
        setLoading,
        controller.signal,
        filterByGroup || undefined
      );
      if (!data || 'success' in data) return;
      setMonitoring(data as IMonitoring[]);
      setMonitoredPlatforms(data as IMonitoring[]);
    })();

    return () => {
      controller.abort();
    };
  }, [filterByGroup]);

  useEffect(() => {
    const controller = new AbortController();

    const getLastTotalScores = async (monitoring: IMonitoring[]) => {
      let scoreEntries: IScoreEntry[] = [];
      for (const value of monitoring) {
        const platformName = PlatformService.getPlatformName(value.platformId, platforms);
        const lastScores = await ScoreService.getLastScores(
          platformName,
          value.company,
          undefined,
          controller.signal
        );

        if (!Array.isArray(lastScores)) return;
        scoreEntries = [...scoreEntries, ...lastScores];
      }
      setLastTotalScores(scoreEntries);
    };

    if (monitoring.length > 0) {
      (async () => await getLastTotalScores(monitoring))();
    }

    return () => {
      controller.abort();
    };
  }, [monitoring, platforms]);

  useEffect(() => {
    if (sort === '') {
      setMonitoredPlatforms(monitoring);
    }
    if (sort === '1') {
      setMonitoredPlatforms((prevState) => {
        return [...prevState].sort((a, b) =>
          PlatformService.getPlatformName(a.platformId, platforms).toLowerCase() >
          PlatformService.getPlatformName(b.platformId, platforms).toLowerCase()
            ? 1
            : -1
        );
      });
    } else if (sort === '2') {
      setMonitoredPlatforms((prevState) => {
        return [...prevState].sort((a, b) =>
          PlatformService.getPlatformName(a.platformId, platforms).toLowerCase() <
          PlatformService.getPlatformName(b.platformId, platforms).toLowerCase()
            ? 1
            : -1
        );
      });
    } else if (sort === '3') {
      setMonitoredPlatforms((prevState) => {
        return [...prevState].sort((a, b) => {
          const { score: scoreA } = getTotalScoreAndReviewCount(a);
          const { score: scoreB } = getTotalScoreAndReviewCount(b);

          return scoreA > scoreB ? 1 : -1;
        });
      });
    } else if (sort === '4') {
      setMonitoredPlatforms((prevState) => {
        return [...prevState].sort((a, b) => {
          const { score: scoreA } = getTotalScoreAndReviewCount(a);
          const { score: scoreB } = getTotalScoreAndReviewCount(b);

          return scoreA < scoreB ? 1 : -1;
        });
      });
    } else if (sort === '5') {
      setMonitoredPlatforms((prevState) => {
        return [...prevState].sort((a, b) => {
          const { reviewCount: reviewCountA } = getTotalScoreAndReviewCount(a);
          const { reviewCount: reviewCountB } = getTotalScoreAndReviewCount(b);

          return reviewCountA > reviewCountB ? 1 : -1;
        });
      });
    } else if (sort === '6') {
      setMonitoredPlatforms((prevState) => {
        return [...prevState].sort((a, b) => {
          const { reviewCount: reviewCountA } = getTotalScoreAndReviewCount(a);
          const { reviewCount: reviewCountB } = getTotalScoreAndReviewCount(b);

          return reviewCountA < reviewCountB ? 1 : -1;
        });
      });
    } else if (sort === '7') {
      setMonitoredPlatforms((prevState) => {
        return [...prevState].sort((a, b) => {
          const { growthPercentage: growthPercentageA } = getTotalScoreAndReviewCount(a);
          const { growthPercentage: growthPercentageB } = getTotalScoreAndReviewCount(b);

          return growthPercentageA > growthPercentageB ? 1 : -1;
        });
      });
    } else if (sort === '8') {
      setMonitoredPlatforms((prevState) => {
        return [...prevState].sort((a, b) => {
          const { growthPercentage: growthPercentageA } = getTotalScoreAndReviewCount(a);
          const { growthPercentage: growthPercentageB } = getTotalScoreAndReviewCount(b);

          return growthPercentageA < growthPercentageB ? 1 : -1;
        });
      });
    }
  }, [sort, monitoring, platforms, getTotalScoreAndReviewCount]);

  const filteredPlatforms = monitoredPlatforms
    .filter((input) => {
      return (
        !filterByCompanyName ||
        (filterByCompanyName.company === input.company &&
          filterByCompanyName.platformId === input.platformId)
      );
    })
    .filter((input) => {
      const platformLabel = PlatformService.getPlatformName(input.platformId, platforms);
      return filterByPlatform === platformLabel || filterByPlatform === '';
    })
    .filter((input) => {
      const { score } = getTotalScoreAndReviewCount(input);
      if (filterStars[0] === 1 && filterStars[1] === 5) {
        return true;
      }
      if (!showZeroReviews) {
        return score > 0;
      }
      return score >= filterStars[0] && score <= filterStars[1];
    })
    .filter((input) => {
      if (filterByType === 'all') {
        return true;
      } else if (filterByType === 'own') {
        return !input.isBenchmark;
      } else {
        return input.isBenchmark;
      }
    });

  const reloadMonitoring = useCallback(async () => {
    const controller = new AbortController();
    const data = await ReviewMonitoringService.getMonitoring(
      true,
      'both',
      setLoading,
      controller.signal,
      filterByGroup || undefined
    );
    if (!data || 'success' in data) return;
    setMonitoring(data as IMonitoring[]);
    setMonitoredPlatforms(data as IMonitoring[]);
    setEditMonitoringEntry(null);
  }, [filterByGroup]);

  const handleDeleteMonitoring = async () => {
    if (!deleteMonitoringId) return;
    const { success } = await ReviewMonitoringService.deleteMonitoring(deleteMonitoringId);
    if (success) {
      setDeleteMonitoringId(null);
      await reloadMonitoring();
    }
  };

  const renderedPlatforms = filteredPlatforms.map((monitoringEntry) => {
    const platformName = PlatformService.getPlatformName(
      monitoringEntry.platformId,
      platforms
    ).toLowerCase();
    let { score, reviewCount, growthPercentage, employeeReviewCount, applicantReviewCount } =
      getTotalScoreAndReviewCount(monitoringEntry);

    const tags = monitoringEntry.tags ?? [];

    return (
      <React.Fragment key={monitoringEntry.id}>
        <Platform
          monitoringId={monitoringEntry.id}
          id={monitoringEntry.platformId}
          platform={platformName}
          companyUrl={monitoringEntry.url}
          company={monitoringEntry.company}
          label={monitoringEntry.label}
          score={score || 0}
          applicantReviews={applicantReviewCount}
          employeeReviews={employeeReviewCount}
          reviews={reviewCount}
          growth={growthPercentage}
          isBenchmark={monitoringEntry.isBenchmark}
          createdAt={monitoringEntry.createdAt}
          skipBackdated={
            PlatformService.getLocalPlatform(monitoringEntry.platformId, platforms).skipBackdated
          }
          skipBackdatedAbove={
            PlatformService.getLocalPlatform(monitoringEntry.platformId, platforms)
              .skipBackdatedAbove
          }
          onEdit={hasRole('can-edit') ? () => setEditMonitoringEntry(monitoringEntry) : undefined}
          onDelete={
            hasRole('can-edit') ? () => setDeleteMonitoringId(monitoringEntry.id) : undefined
          }
          tags={tags}
        />
      </React.Fragment>
    );
  });

  return (
    <Section>
      <>
        {platforms.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              borderRadius: `${theme.shape.borderRadius}px`,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <img
              width={300}
              src={MonitoringNoContent}
              alt="Monitoring no records"
            />
            <Typography sx={{ mt: theme.spacing(4) }}>{t('no_records')}</Typography>
            {hasRole('can-add') && !exceedsLimit('limitmonitoring', 1) && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => navigate('guide')}
                sx={{ mt: theme.spacing(2) }}
              >
                {t('add_monitoring')}
              </Button>
            )}
          </Box>
        ) : (
          <>
            {loading ? (
              <LinearProgress />
            ) : (
              <ContentContainer>
                <LeftContainer>
                  <ActionBar
                    children={
                      <AddEditContainer>
                        {hasRole('can-add') &&
                          !exceedsLimit('limitmonitoring', monitoredPlatforms.length + 1) && (
                            <Button
                              variant="contained"
                              startIcon={<Add />}
                              onClick={() => navigate('guide')}
                            >
                              {t('add_monitoring')}
                            </Button>
                          )}
                      </AddEditContainer>
                    }
                  />
                  <SortContainer sx={{ mb: 0 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ my: theme.spacing(2) }}
                    >
                      {getRecordCount()}
                    </Typography>
                    <FormControl
                      size="small"
                      sx={{ minWidth: '284px' }}
                    >
                      <InputLabel id="sort-select-label">{t('sort')}</InputLabel>
                      <Select
                        labelId="sort-select-label"
                        value={sort}
                        onChange={(event: SelectChangeEvent) => {
                          setSort(event.target.value);
                        }}
                        label={t('sort')}
                      >
                        <MenuItem value="">
                          <em>{t('unsorted')}</em>
                        </MenuItem>
                        <MenuItem value="1">{t('platforms_asc')}</MenuItem>
                        <MenuItem value="2">{t('platforms_desc')}</MenuItem>
                        <MenuItem value="3">{t('score_asc')}</MenuItem>
                        <MenuItem value="4">{t('score_desc')}</MenuItem>
                        <MenuItem value="5">{t('review_count_asc')}</MenuItem>
                        <MenuItem value="6">{t('review_count_desc')}</MenuItem>
                        <MenuItem value="7">{t('growth_percentage_asc')}</MenuItem>
                        <MenuItem value="8">{t('growth_percentage_desc')}</MenuItem>
                      </Select>
                    </FormControl>
                  </SortContainer>
                  {filterByType === 'all' && (
                    <Box
                      sx={{
                        display: 'flex',
                        mb: theme.spacing(2),
                      }}
                    >
                      <Box
                        sx={{
                          width: '6px',
                          height: '100%',
                          background: theme.palette.primary.main,
                        }}
                      />
                      <Typography
                        variant="subtitle2"
                        sx={{ ml: theme.spacing(1) }}
                      >
                        {t('own')}
                      </Typography>
                      <Box
                        sx={{
                          width: '6px',
                          height: '100%',
                          background: theme.palette.secondary.main,
                          ml: theme.spacing(2),
                        }}
                      />
                      <Typography
                        variant="subtitle2"
                        sx={{ ml: theme.spacing(1) }}
                      >
                        {t('benchmarking')}
                      </Typography>
                    </Box>
                  )}
                  <PlatformContainer>{renderedPlatforms}</PlatformContainer>
                </LeftContainer>
                <RightContainer>
                  <Filter
                    filters={
                      <>
                        <MonitoringLabelFilter
                          value={filterByCompanyName}
                          options={monitoringLabelOptions}
                          onChange={setFilterByCompanyName}
                        />
                        <PlatformSelect
                          value={filterByPlatform}
                          onChange={(event: SelectChangeEvent) => {
                            setFilterByPlatform(event.target.value);
                          }}
                          platforms={platforms}
                        />
                        {groups.length > 0 && (
                          <FormControl
                            size="small"
                            sx={{ minWidth: '284px', mt: theme.spacing(1), mb: theme.spacing(1) }}
                          >
                            <InputLabel id="group-filter-label">{t('nav_groups')}</InputLabel>
                            <Select
                              labelId="group-filter-label"
                              value={filterByGroup}
                              onChange={(event: SelectChangeEvent) => {
                                setFilterByGroup(event.target.value);
                              }}
                              label={t('nav_groups')}
                            >
                              <MenuItem value="">
                                <em>-</em>
                              </MenuItem>
                              {groups.map((group) => (
                                <MenuItem
                                  key={group.id}
                                  value={group.id}
                                >
                                  {group.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                        <Typography
                          variant="caption"
                          sx={{
                            textTransform: 'uppercase',
                            fontWeight: '700',
                            mt: theme.spacing(1),
                          }}
                        >
                          {t('score')}
                        </Typography>
                        <SliderStarsAsLabels
                          value={filterStars}
                          onChange={handleFilterStarsChange}
                          sx={{ pb: theme.spacing(2) }}
                        />
                        <FormControl
                          size="small"
                          sx={{ minWidth: '284px', mt: theme.spacing(2), mb: theme.spacing(1) }}
                        >
                          <InputLabel id="benchmarking-filter-select-label">
                            {t('record_type')}
                          </InputLabel>
                          <Select
                            labelId="benchmarking-filter-select-label"
                            value={filterByType}
                            onChange={(event: SelectChangeEvent) => {
                              setFilterByType(event.target.value as MonitoringFilterType);
                            }}
                            label={t('record_type')}
                          >
                            <MenuItem value="all">{t('all_records')}</MenuItem>
                            <MenuItem value="own">{t('own')}</MenuItem>
                            <MenuItem value="benchmarking">{t('benchmarking')}</MenuItem>
                          </Select>
                        </FormControl>
                      </>
                    }
                    activeFilters={
                      <>
                        {(filterStars[0] !== 1 || filterStars[1] !== 5) && (
                          <Chip
                            label={`${t('stars')} ${filterStars[0]} - ${filterStars[1]}`}
                            onDelete={() => setFilterStars([1, 5])}
                          />
                        )}
                        {filterByPlatform !== '' && (
                          <Chip
                            label={filterByPlatform}
                            onDelete={() => {
                              setFilterByPlatform('');
                            }}
                          />
                        )}
                        {filterByType !== 'all' ? (
                          <Chip
                            label={filterByTypeLabel}
                            onDelete={() => {
                              setFilterByType('all');
                            }}
                          />
                        ) : (
                          <Chip label={filterByTypeLabel} />
                        )}
                        {filterByGroup !== '' && (
                          <Chip
                            label={`${t('nav_groups')}: ${
                              groups.find((g) => g.id === filterByGroup)?.name ?? ''
                            }`}
                            onDelete={() => setFilterByGroup('')}
                          />
                        )}
                      </>
                    }
                    resetActiveFilters={() => {
                      setFilterStars([1, 5]);
                      setFilterByPlatform('');
                      setFilterByType('all');
                      setFilterByGroup('');
                    }}
                  />
                </RightContainer>
              </ContentContainer>
            )}
          </>
        )}
      </>

      {/* Edit monitoring dialog (step 65-76) */}
      <Dialog
        open={editMonitoringEntry !== null}
        onClose={() => setEditMonitoringEntry(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('edit_monitoring')}</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {editMonitoringEntry && (
            <EditMonitoringCard
              id={editMonitoringEntry.id}
              companyName={editMonitoringEntry.company}
              label={editMonitoringEntry.label}
              minDaysPast={editMonitoringEntry.minDaysPast}
              createdAt={editMonitoringEntry.createdAt}
              updatedAt={editMonitoringEntry.updatedAt}
              jobs={editMonitoringEntry.jobs}
              url={editMonitoringEntry.url}
              communitySettings={editMonitoringEntry.communityManagement}
              platformId={editMonitoringEntry.platformId}
              platformLabel={
                platforms.find((platform) => platform.id === editMonitoringEntry.platformId)?.label
              }
              reload={reloadMonitoring}
              defaultEdit
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete monitoring confirm dialog (step 65) */}
      <Confirm
        text={t('delete_record_confirm')}
        open={deleteMonitoringId !== null}
        onCancel={() => setDeleteMonitoringId(null)}
        onOk={handleDeleteMonitoring}
      />
    </Section>
  );
};

export default Platforms;
